import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useConfirm } from 'legacy/lut/components/shared/Confirm/useConfirm'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { FC, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { setPublishingMode } from 'core/store/appSlice'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { IAssortmentCategory } from 'partner360/features/assortment/models/data/IAssortmentCategory'
import { IAssortmentGroup } from 'partner360/features/assortment/models/data/IAssortmentGroup'
import { IAssortmentItem } from 'partner360/features/assortment/models/data/IAssortmentItem'
import {
  selectAssortmentEditingGroupId,
  selectIsLoading,
} from 'partner360/features/assortment/selectors'
import {
  requestRemoveAssortmentGroup,
  requestRemoveAssortmentItem,
} from 'partner360/features/assortment/thunks'

import { AddAssortmentGroup } from '../../components/addAssortmentGroup/AddAssortmentGroup'
import { AddAssortmentItem } from '../../components/addAssortmentItem/AddAssortmentItem'
import { AssortmentGroup } from '../../components/assortmentGroup/AssortmentGroup'
import { AssortmentItem } from '../../components/assortmentItem/AssortmentItem'
import {
  DeleteConfirmDialog,
  deleteConfirmDialogIdentifier,
} from '../../components/deleteConfirmDialog/DeleteConfirmDialog'
import { GroupContent } from '../../components/groupContent/GroupContent'
import { useSaveAssortmentData } from '../../hooks/useSaveAssortmentData'
import { useSaveAssortmentOrder } from '../../hooks/useSaveAssrotmentOrder'
import { TRANSLATION_KEYS } from '../../translations'
import { NotApplicableCategory } from '../notApplicableCategory/NotApplicableCategory'

import { useCategoryAssortment } from './hooks/useCategoryAssortment'

import styles from './CategoryAssortmentContent.module.scss'

interface ICategoryAssortmentContentProps {
  isEmbedded: boolean
  fYear: number
  partnerId: string
  programType: ProgramTypes
  selectedCategory: string
  isApplicable?: boolean
}

export const CategoryAssortmentContent: FC<ICategoryAssortmentContentProps> = ({
  isEmbedded,
  fYear,
  partnerId,
  programType,
  selectedCategory,
  isApplicable = true,
}) => {
  const isLoading = useSelector(selectIsLoading)
  const dispatch = useAppDispatch()
  const [deleteMessage, setDeleteMessage] = useState('')

  const { confirm } = useConfirm()
  const { isSaving, saveChanges } = useSaveAssortmentData({
    fYear,
    partnerId,
    programType,
    category: selectedCategory,
  })
  const { saveGroupsOrder, saveItemsOrder } = useSaveAssortmentOrder({
    fYear,
    partnerId,
    programType,
    category: selectedCategory,
  })

  const handleDeleteItem = useCallback(
    async (item: IAssortmentItem) => {
      setDeleteMessage(TRANSLATION_KEYS.deleteConfirm.deleteItemMessage)
      if (await confirm(deleteConfirmDialogIdentifier)) {
        dispatch(requestRemoveAssortmentItem(item.EntryId))
      }
    },
    [dispatch]
  )

  const handleDeleteGroup = useCallback(
    async (group: IAssortmentGroup) => {
      setDeleteMessage(TRANSLATION_KEYS.deleteConfirm.deleteGroupMessage)
      if (await confirm(deleteConfirmDialogIdentifier)) {
        dispatch(requestRemoveAssortmentGroup(group.EntryId))
      }
    },
    [dispatch]
  )

  const groups = useCategoryAssortment({
    partnerId,
    programType,
    fYear,
    categoryName: selectedCategory,
  })

  const editingGroupId = useSelector(selectAssortmentEditingGroupId)
  const isEditingMode = useSelector(selectPublishingMode)
  const isEmptyState = !isLoading && !isSaving && !isEditingMode && groups.length === 0

  const handleEnableEditMode = useCallback(() => {
    dispatch(setPublishingMode(true))
    if (!isEmbedded) {
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
      }, 100)
    }
  }, [dispatch, isEmbedded])

  if (!isApplicable) {
    return <NotApplicableCategory category={selectedCategory} />
  }

  return (
    <div className={styles.content}>
      <LoaderOverlay isVisible={isLoading || isSaving} />
      {isEmptyState && (
        <div className={styles.emptyState}>
          <Translation span text={TRANSLATION_KEYS.emptyState.message} />
          <Button variant="outlined" onClick={handleEnableEditMode}>
            <Translation span text={TRANSLATION_KEYS.emptyState.button} />
          </Button>
        </div>
      )}
      {!isEmptyState &&
        groups.map((group, index) => {
          const isEditingGroup = group.EntryId === editingGroupId
          return (
            <div className={styles.group} key={group.EntryId}>
              <AssortmentGroup
                index={index}
                card={group}
                disableDragging={isEditingGroup}
                isEditingGroup={isEditingGroup}
                onDeleteCard={handleDeleteGroup}
                onSave={saveChanges}
                onSaveOrder={saveGroupsOrder}
              />
              <GroupContent groupId={group.EntryId}>
                {group.children.map((item, index) => {
                  const isParentEditing = isEditingGroup
                  return (
                    <AssortmentItem
                      key={item.EntryId}
                      card={item}
                      findItemById={() => ({ item, index })}
                      index={index}
                      isEditing={isParentEditing}
                      disableDragging={isParentEditing}
                      onDeleteCard={handleDeleteItem}
                      onSaveOrder={saveItemsOrder}
                    />
                  )
                })}
                {isEditingGroup && <AddAssortmentItem assortmentGroupId={editingGroupId!} />}
              </GroupContent>
            </div>
          )
        })}
      <RenderWhen condition={isEditingMode}>
        <AddAssortmentGroup
          fYear={fYear}
          partnerId={partnerId}
          category={selectedCategory}
          programType={programType}
        />
      </RenderWhen>
      <DeleteConfirmDialog messageKey={deleteMessage} />
    </div>
  )
}
